import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { updateCashflowSettings } from "@/api/cashflow";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SELECT_CLASSES } from "@/lib/styles";
import { errorDetail, parseInnList } from "@/pages/cashflow/utils";
import type { CashflowSettingsView } from "@/types/api";

function splitInns(text: string): string[] {
  return text
    .split(/[\n,;]/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/** Текст → список значений (склад/код/префикс на строку), без пустых. */
function splitLines(text: string): string[] {
  return text
    .split("\n")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/** Первое значение списка, встречающееся дважды (после нормализации) — `null`, если дублей нет. */
function firstDuplicate(items: string[]): string | null {
  const seen = new Set<string>();
  for (const item of items) {
    if (seen.has(item)) return item;
    seen.add(item);
  }
  return null;
}

/**
 * Zod-проверка списка из textarea на дубли — бэк отвечает 422 на дубль,
 * и без этой проверки пользователь видит сырую ошибку сервера.
 *
 * `normalize` — как бэк сравнивает значения (для префиксов — `toLowerCase`,
 * он же и хранит их в нижнем регистре).
 */
function noDuplicateLines(normalize: (v: string) => string = (v) => v) {
  return (text: string, ctx: z.RefinementCtx): void => {
    const dup = firstDuplicate(splitLines(text).map(normalize));
    if (dup !== null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Повторяется: ${dup}` });
    }
  };
}

/** Целое в текстовом поле: пустое/дробное/за границами — громкая ошибка формы. */
function intText(label: string, min: number, max: number) {
  return z.string().refine((v) => {
    const n = Number(v.trim());
    return v.trim() !== "" && Number.isInteger(n) && n >= min && n <= max;
  }, `${label}: целое число от ${min} до ${max}`);
}

const schema = z
  .object({
    org_inns_text: z
      .string()
      .refine((v) => splitInns(v).length > 0, "Нужен хотя бы один ИНН")
      .refine(
        (v) => splitInns(v).every((inn) => /^(\d{10}|\d{12})$/.test(inn)),
        "Каждый ИНН — 10 цифр (юр. лицо) или 12 (ИП), по одному в строке",
      ),
    // Список может быть пустым (внутригрупповых контрагентов нет), но мусор в нём
    // — ошибка формы: молча выбросить непонятный токен значит потерять ИНН.
    intercompany_inns_text: z.string().superRefine((v, ctx) => {
      try {
        parseInnList(v);
      } catch (e) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: (e as Error).message });
      }
    }),
    horizon_weeks: intText("Горизонт", 1, 52),
    payment_order_max_age_days: intText("Платёжки не старше", 0, 3650),
    open_order_max_age_days: intText("Заказы не старше", 0, 3650),
    salary_advance_share: z.string().refine((v) => {
      const n = Number(v.trim().replace(",", "."));
      return v.trim() !== "" && Number.isFinite(n) && n >= 0 && n <= 1;
    }, "Доля аванса — число от 0 до 1 (например 0.4)"),
    // Пустая строка — «— выключено —» (production_org_inn = null на бэке).
    production_org_inn: z.string(),
    production_warehouses_text: z.string().superRefine(noDuplicateLines()),
    production_order_max_age_days: intText("Заказы на производство не старше", 0, 3650),
    dead_order_after_days: intText("Заказ мёртв при просрочке прихода", 0, 3650),
    price_lookback_days: intText("Цена из приходных за", 1, 3650),
    price_excluded_supplier_codes_text: z.string().superRefine(noDuplicateLines()),
    force_explode_codes_text: z.string().superRefine(noDuplicateLines()),
    excluded_material_name_prefixes_text: z
      .string()
      .superRefine(noDuplicateLines((v) => v.toLowerCase())),
  })
  .superRefine((v, ctx) => {
    if (v.production_org_inn === "") return;
    if (!splitInns(v.org_inns_text).includes(v.production_org_inn)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["production_org_inn"],
        message: "Организация производства должна быть в списке организаций плана",
      });
    }
    if (splitLines(v.production_warehouses_text).length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["production_warehouses_text"],
        message: "Заданы закупки под производство — нужен хотя бы один склад",
      });
    }
  });

type Values = z.infer<typeof schema>;

interface Props {
  settings: CashflowSettingsView;
  onClose: () => void;
}

export function SettingsDialog({ settings, onClose }: Props): React.ReactElement {
  const qc = useQueryClient();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      org_inns_text: settings.org_inns.join("\n"),
      intercompany_inns_text: settings.intercompany_inns.join("\n"),
      horizon_weeks: String(settings.horizon_weeks),
      payment_order_max_age_days: String(settings.payment_order_max_age_days),
      open_order_max_age_days: String(settings.open_order_max_age_days),
      salary_advance_share: settings.salary_advance_share,
      production_org_inn: settings.production_org_inn ?? "",
      production_warehouses_text: settings.production_warehouses.join("\n"),
      production_order_max_age_days: String(settings.production_order_max_age_days),
      dead_order_after_days: String(settings.dead_order_after_days),
      price_lookback_days: String(settings.price_lookback_days),
      price_excluded_supplier_codes_text: settings.price_excluded_supplier_codes.join("\n"),
      force_explode_codes_text: settings.force_explode_codes.join("\n"),
      excluded_material_name_prefixes_text: settings.excluded_material_name_prefixes.join("\n"),
    },
  });

  // Список организаций-кандидатов для выпадающего списка — из ЖИВОГО значения
  // поля «ИНН организаций»: правка списка выше сразу отражается в опциях ниже.
  const orgInnOptions = splitInns(watch("org_inns_text"));

  const mut = useMutation({
    mutationFn: (v: Values) =>
      updateCashflowSettings({
        org_inns: splitInns(v.org_inns_text),
        intercompany_inns: parseInnList(v.intercompany_inns_text),
        horizon_weeks: Number(v.horizon_weeks.trim()),
        payment_order_max_age_days: Number(v.payment_order_max_age_days.trim()),
        open_order_max_age_days: Number(v.open_order_max_age_days.trim()),
        salary_advance_share: v.salary_advance_share.trim().replace(",", "."),
        production_org_inn: v.production_org_inn === "" ? null : v.production_org_inn,
        production_warehouses: splitLines(v.production_warehouses_text),
        production_order_max_age_days: Number(v.production_order_max_age_days.trim()),
        dead_order_after_days: Number(v.dead_order_after_days.trim()),
        price_lookback_days: Number(v.price_lookback_days.trim()),
        price_excluded_supplier_codes: splitLines(v.price_excluded_supplier_codes_text),
        force_explode_codes: splitLines(v.force_explode_codes_text),
        excluded_material_name_prefixes: splitLines(v.excluded_material_name_prefixes_text),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cashflow-settings"] });
      qc.invalidateQueries({ queryKey: ["cashflow-calendar"] });
      // Сверка дебиторки считает по этому же списку ИНН — её кэш тоже протух.
      qc.invalidateQueries({ queryKey: ["cashflow-receivables-check"] });
      toast.success("Настройки сохранены");
      onClose();
    },
    onError: (err: unknown) =>
      toast.error("Не удалось сохранить настройки", { description: errorDetail(err) }),
  });

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Настройки финплана</DialogTitle>
          <DialogDescription>
            Организации плана (по ИНН), глубина горизонта и пороги «свежести» документов
            1С. Настройки общие для всей компании.
          </DialogDescription>
        </DialogHeader>

        <form
          id="cashflow-settings-form"
          className="space-y-4 py-2"
          onSubmit={handleSubmit((v) => mut.mutate(v))}
        >
          <div className="space-y-1.5">
            <Label htmlFor="org_inns_text">ИНН организаций (по одному в строке)</Label>
            <textarea
              id="org_inns_text"
              rows={4}
              className={`${SELECT_CLASSES} h-auto font-mono`}
              {...register("org_inns_text")}
            />
            {errors.org_inns_text && (
              <p className="text-xs text-rose-600">{errors.org_inns_text.message}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="intercompany_inns_text">
              Внутригрупповые ИНН (свои же юрлица — в сверку дебиторки не идут)
            </Label>
            <textarea
              id="intercompany_inns_text"
              rows={3}
              className={`${SELECT_CLASSES} h-auto font-mono`}
              {...register("intercompany_inns_text")}
            />
            {errors.intercompany_inns_text && (
              <p className="text-xs text-rose-600">{errors.intercompany_inns_text.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="horizon_weeks">Горизонт, недель</Label>
              <Input id="horizon_weeks" {...register("horizon_weeks")} />
              {errors.horizon_weeks && (
                <p className="text-xs text-rose-600">{errors.horizon_weeks.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="salary_advance_share">Доля аванса зарплаты</Label>
              <Input id="salary_advance_share" {...register("salary_advance_share")} />
              {errors.salary_advance_share && (
                <p className="text-xs text-rose-600">{errors.salary_advance_share.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="payment_order_max_age_days">Платёжки не старше, дней</Label>
              <Input
                id="payment_order_max_age_days"
                {...register("payment_order_max_age_days")}
              />
              {errors.payment_order_max_age_days && (
                <p className="text-xs text-rose-600">
                  {errors.payment_order_max_age_days.message}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="open_order_max_age_days">Заказы не старше, дней</Label>
              <Input id="open_order_max_age_days" {...register("open_order_max_age_days")} />
              {errors.open_order_max_age_days && (
                <p className="text-xs text-rose-600">
                  {errors.open_order_max_age_days.message}
                </p>
              )}
            </div>
          </div>

          <div className="space-y-3 border-t pt-3">
            <div className="space-y-1">
              <h3 className="text-sm font-semibold">Закупки под план производства</h3>
              <p className="text-xs text-muted-foreground">
                Считается только при организации производства; остатки — по перечисленным
                складам.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="production_org_inn">Организация производства</Label>
              <select
                id="production_org_inn"
                className={SELECT_CLASSES}
                {...register("production_org_inn")}
              >
                <option value="">— выключено —</option>
                {orgInnOptions.map((inn) => (
                  <option key={inn} value={inn}>
                    {inn}
                  </option>
                ))}
              </select>
              {errors.production_org_inn && (
                <p className="text-xs text-rose-600">{errors.production_org_inn.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="production_warehouses_text">Склады (по одному в строке)</Label>
              <textarea
                id="production_warehouses_text"
                rows={3}
                className={`${SELECT_CLASSES} h-auto font-mono`}
                {...register("production_warehouses_text")}
              />
              {errors.production_warehouses_text && (
                <p className="text-xs text-rose-600">
                  {errors.production_warehouses_text.message}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="production_order_max_age_days">
                  Заказы на производство не старше, дней
                </Label>
                <Input
                  id="production_order_max_age_days"
                  {...register("production_order_max_age_days")}
                />
                {errors.production_order_max_age_days && (
                  <p className="text-xs text-rose-600">
                    {errors.production_order_max_age_days.message}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="dead_order_after_days">
                  Заказ мёртв при просрочке прихода, дней
                </Label>
                <Input id="dead_order_after_days" {...register("dead_order_after_days")} />
                {errors.dead_order_after_days && (
                  <p className="text-xs text-rose-600">{errors.dead_order_after_days.message}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="price_lookback_days">Цена из приходных за, дней</Label>
                <Input id="price_lookback_days" {...register("price_lookback_days")} />
                {errors.price_lookback_days && (
                  <p className="text-xs text-rose-600">{errors.price_lookback_days.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="price_excluded_supplier_codes_text">
                Исключённые поставщики для цены (коды контрагентов 1С, по одному в строке)
              </Label>
              <textarea
                id="price_excluded_supplier_codes_text"
                rows={2}
                className={`${SELECT_CLASSES} h-auto font-mono`}
                {...register("price_excluded_supplier_codes_text")}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="force_explode_codes_text">
                Всегда разворачивать спецификацию (коды номенклатуры, по одному в строке)
              </Label>
              <textarea
                id="force_explode_codes_text"
                rows={2}
                className={`${SELECT_CLASSES} h-auto font-mono`}
                {...register("force_explode_codes_text")}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="excluded_material_name_prefixes_text">
                Исключённые префиксы наименований материалов (по одному в строке)
              </Label>
              <textarea
                id="excluded_material_name_prefixes_text"
                rows={2}
                className={`${SELECT_CLASSES} h-auto font-mono`}
                {...register("excluded_material_name_prefixes_text")}
              />
            </div>
          </div>
        </form>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" type="button" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" form="cashflow-settings-form" disabled={mut.isPending}>
            {mut.isPending ? "Сохранение…" : "Сохранить"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
